/**
 * ============================================================================
 * [P2-02] useTexts Hook
 *
 * 글(texts) 데이터 관리를 위한 커스텀 훅
 *
 * 기능:
 * - 실시간 구독으로 글 목록 동기화
 * - CRUD 함수 (saveText, editText, removeText)
 * - 페이지네이션 (loadMore)
 * - 필터링 (setFilter)
 *
 * Safety:
 * - useEffect cleanup으로 메모리 누수 방지
 * - 낙관적 업데이트 + 에러 시 롤백
 * - user?.uid 의존성으로 불필요한 재구독 방지
 * ============================================================================
 */

import { useState, useEffect, useCallback } from 'react'
import { useAuth } from './useAuth'
import { useAppStore } from '@/stores'
import {
  createText,
  updateText,
  deleteText,
  getTexts,
  subscribeToTexts,
} from '@/services/firestore'
import type { SavedText, FilterState } from '@/types'
import type { DocumentSnapshot } from 'firebase/firestore'

// ============================================================================
// [P2-02-H] 반환 타입 정의
// ============================================================================
export interface UseTextsReturn {
  // State
  texts: SavedText[]
  deletedTexts: SavedText[]  // [P5-02] 휴지통 항목
  isLoading: boolean
  error: Error | null
  hasMore: boolean

  // CRUD
  saveText: (text: Omit<SavedText, 'id' | 'createdAt' | 'updatedAt'>) => Promise<SavedText | null>
  editText: (id: string, updates: Partial<SavedText>) => Promise<boolean>
  removeText: (id: string) => Promise<boolean>
  toggleFavorite: (id: string) => Promise<boolean>  // [P3-01] 즐겨찾기 토글
  toggleUsed: (id: string) => Promise<boolean>      // 레퍼런스 사용 상태 토글

  // [P5-02] 휴지통
  restoreText: (id: string) => Promise<boolean>
  permanentDeleteText: (id: string) => Promise<boolean>

  // Pagination
  loadMore: () => Promise<void>
  refresh: () => Promise<void>

  // Filter
  setFilter: (filter: Partial<FilterState>) => void
}

// ============================================================================
// [P2-02-A] useTexts Hook 구현
// ============================================================================
export function useTexts(): UseTextsReturn {
  // ========================================================================
  // Auth 상태 가져오기
  // ========================================================================
  const { user } = useAuth()

  // ========================================================================
  // Store 상태 & 액션 가져오기 (개별 selector로 리렌더링 최적화)
  // ========================================================================
  const texts = useAppStore((state) => state.savedTexts.texts)
  const filter = useAppStore((state) => state.savedTexts.filter)
  const isLoading = useAppStore((state) => state.savedTexts.isLoading)

  const setSavedTexts = useAppStore((state) => state.setSavedTexts)
  const setSavedTextsLoading = useAppStore((state) => state.setSavedTextsLoading)
  const setSavedTextsFilter = useAppStore((state) => state.setSavedTextsFilter)
  const addSavedText = useAppStore((state) => state.addSavedText)
  const updateSavedText = useAppStore((state) => state.updateSavedText)
  const deleteSavedText = useAppStore((state) => state.deleteSavedText)

  // ========================================================================
  // Local State
  // ========================================================================
  const [error, setError] = useState<Error | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null)

  // ========================================================================
  // [P2-02-B] 실시간 구독 useEffect
  // - CRITICAL: cleanup에서 unsubscribe 호출 필수
  // - 의존성: user?.uid만 사용 (user 객체 전체를 사용하면 불필요한 재구독 발생)
  // ========================================================================
  useEffect(() => {
    // 1. 로그아웃 상태 처리
    if (!user) {
      setSavedTexts([])
      setSavedTextsLoading(false)
      setError(null)
      setHasMore(true)
      setLastDoc(null)
      return
    }

    // 2. 로딩 시작
    setSavedTextsLoading(true)

    // 3. 구독 시작
    const unsubscribe = subscribeToTexts(
      user.uid,
      (newTexts) => {
        setSavedTexts(newTexts)
        setSavedTextsLoading(false)
        setError(null)
      },
      (err) => {
        setError(err)
        setSavedTextsLoading(false)
      }
    )

    // 4. CRITICAL: Cleanup - 메모리 누수 방지
    return () => {
      unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid])
  // Note: user?.uid만 의존성에 포함하여 uid 변경 시에만 재구독
  // setSavedTexts, setSavedTextsLoading은 Zustand store에서 안정적인 참조이므로 제외

  // ========================================================================
  // [P2-02-C] saveText 함수 (Create)
  // ========================================================================
  const saveText = useCallback(
    async (
      textData: Omit<SavedText, 'id' | 'createdAt' | 'updatedAt'>
    ): Promise<SavedText | null> => {
      // 1. Auth Check
      if (!user) {
        console.warn('[useTexts.saveText] User not authenticated')
        return null
      }

      // 2. Validation
      if (!textData.content?.trim()) {
        setError(new Error('Content is required'))
        return null
      }

      try {
        // 3. Create in Firestore
        const newText = await createText(user.uid, {
          ...textData,
          charCount: textData.content.length,
        })

        // 4. 실시간 구독이 Store 업데이트 처리함
        // addSavedText(newText) 호출 불필요

        setError(null)
        return newText
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error')
        setError(error)
        console.error('[useTexts.saveText] Error:', error)
        return null
      }
    },
    [user]
  )

  // ========================================================================
  // [P2-02-D] editText 함수 (Update) - 낙관적 업데이트
  // ========================================================================
  const editText = useCallback(
    async (id: string, updates: Partial<SavedText>): Promise<boolean> => {
      // 1. Auth Check
      if (!user) {
        console.warn('[useTexts.editText] User not authenticated')
        return false
      }

      // 2. Validation
      if (!id) {
        setError(new Error('Text ID is required'))
        return false
      }

      // 3. 기존 데이터 백업 (롤백용)
      const originalText = texts.find((t) => t.id === id)
      if (!originalText) {
        setError(new Error('Text not found'))
        return false
      }

      try {
        // 4. 낙관적 업데이트 (즉시 UI 반영)
        updateSavedText(id, updates)

        // 5. Firestore 업데이트
        await updateText(user.uid, id, updates)

        setError(null)
        return true
      } catch (err) {
        // 6. 롤백 - 원본 데이터로 복원
        updateSavedText(id, originalText)

        const error = err instanceof Error ? err : new Error('Unknown error')
        setError(error)
        console.error('[useTexts.editText] Error:', error)
        return false
      }
    },
    [user, texts, updateSavedText]
  )

  // ========================================================================
  // [P2-02-E] removeText 함수 (Soft Delete) - 휴지통으로 이동
  // ========================================================================
  const removeText = useCallback(
    async (id: string): Promise<boolean> => {
      console.log('[useTexts.removeText] 삭제 시작, id:', id)

      // 1. Auth Check
      if (!user) {
        console.warn('[useTexts.removeText] User not authenticated')
        alert('로그인이 필요합니다.')
        return false
      }

      // 2. Validation
      if (!id) {
        setError(new Error('Text ID is required'))
        return false
      }

      // 3. 기존 데이터 백업 (롤백용)
      const originalText = texts.find((t) => t.id === id)
      if (!originalText) {
        console.error('[useTexts.removeText] Text not found:', id)
        setError(new Error('Text not found'))
        return false
      }

      console.log('[useTexts.removeText] 삭제할 텍스트:', originalText.title)

      try {
        // 4. 낙관적 업데이트 - 소프트 삭제 (휴지통으로 이동)
        const softDeleteData = {
          isDeleted: true,
          deletedAt: new Date().toISOString(),
        }
        updateSavedText(id, softDeleteData)

        // 5. Firestore 업데이트 (소프트 삭제)
        console.log('[useTexts.removeText] Firestore 업데이트 시작')
        await updateText(user.uid, id, softDeleteData)
        console.log('[useTexts.removeText] Firestore 업데이트 완료')

        setError(null)
        return true
      } catch (err) {
        // 6. 롤백 - 원본 데이터로 복원
        updateSavedText(id, { isDeleted: false, deletedAt: undefined })

        const error = err instanceof Error ? err : new Error('Unknown error')
        setError(error)
        console.error('[useTexts.removeText] Error:', error)
        return false
      }
    },
    [user, texts, updateSavedText]
  )

  // ========================================================================
  // [P5-02] restoreText 함수 (휴지통에서 복원)
  // ========================================================================
  const restoreText = useCallback(
    async (id: string): Promise<boolean> => {
      // 1. Auth Check
      if (!user) {
        console.warn('[useTexts.restoreText] User not authenticated')
        return false
      }

      // 2. Validation
      if (!id) {
        setError(new Error('Text ID is required'))
        return false
      }

      try {
        // 3. 낙관적 업데이트 - 복원
        const restoreData = {
          isDeleted: false,
          deletedAt: undefined,
        }
        updateSavedText(id, restoreData)

        // 4. Firestore 업데이트 (deletedAt 필드 제거를 위해 undefined 전달)
        await updateText(user.uid, id, { isDeleted: false, deletedAt: undefined })

        setError(null)
        return true
      } catch (err) {
        // 5. 롤백
        updateSavedText(id, { isDeleted: true })

        const error = err instanceof Error ? err : new Error('Unknown error')
        setError(error)
        console.error('[useTexts.restoreText] Error:', error)
        return false
      }
    },
    [user, updateSavedText]
  )

  // ========================================================================
  // [P5-02] permanentDeleteText 함수 (영구 삭제)
  // ========================================================================
  const permanentDeleteText = useCallback(
    async (id: string): Promise<boolean> => {
      // 1. Auth Check
      if (!user) {
        console.warn('[useTexts.permanentDeleteText] User not authenticated')
        return false
      }

      // 2. Validation
      if (!id) {
        setError(new Error('Text ID is required'))
        return false
      }

      // 3. 기존 데이터 백업 (롤백용)
      const originalText = texts.find((t) => t.id === id)

      try {
        // 4. 낙관적 삭제 (즉시 UI 반영)
        deleteSavedText(id)

        // 5. Firestore 영구 삭제
        await deleteText(user.uid, id)

        setError(null)
        return true
      } catch (err) {
        // 6. 롤백 - 삭제된 항목 복원
        if (originalText) {
          addSavedText(originalText)
        }

        const error = err instanceof Error ? err : new Error('Unknown error')
        setError(error)
        console.error('[useTexts.permanentDeleteText] Error:', error)
        return false
      }
    },
    [user, texts, deleteSavedText, addSavedText]
  )

  // ========================================================================
  // [P3-01] toggleFavorite 함수 (즐겨찾기 토글) - 낙관적 업데이트
  // ========================================================================
  const toggleFavorite = useCallback(
    async (id: string): Promise<boolean> => {
      // 1. Auth Check
      if (!user) {
        console.warn('[useTexts.toggleFavorite] User not authenticated')
        return false
      }

      // 2. Validation
      if (!id) {
        setError(new Error('Text ID is required'))
        return false
      }

      // 3. 현재 상태 확인
      const targetText = texts.find((t) => t.id === id)
      if (!targetText) {
        setError(new Error('Text not found'))
        return false
      }

      const newFavoriteState = !targetText.isFavorite

      try {
        // 4. 낙관적 업데이트 (즉시 UI 반영)
        updateSavedText(id, { isFavorite: newFavoriteState })

        // 5. Firestore 업데이트
        await updateText(user.uid, id, { isFavorite: newFavoriteState })

        setError(null)
        return true
      } catch (err) {
        // 6. 롤백 - 원본 상태로 복원
        updateSavedText(id, { isFavorite: targetText.isFavorite })

        const error = err instanceof Error ? err : new Error('Unknown error')
        setError(error)
        console.error('[useTexts.toggleFavorite] Error:', error)
        return false
      }
    },
    [user, texts, updateSavedText]
  )

  // ========================================================================
  // toggleUsed 함수 (레퍼런스 사용 상태 토글) - 낙관적 업데이트
  // ========================================================================
  const toggleUsed = useCallback(
    async (id: string): Promise<boolean> => {
      if (!user) {
        console.warn('[useTexts.toggleUsed] User not authenticated')
        return false
      }

      if (!id) {
        setError(new Error('Text ID is required'))
        return false
      }

      const targetText = texts.find((t) => t.id === id)
      if (!targetText) {
        setError(new Error('Text not found'))
        return false
      }

      const newUsedState = !targetText.isUsed

      try {
        updateSavedText(id, { isUsed: newUsedState })
        await updateText(user.uid, id, { isUsed: newUsedState })
        setError(null)
        return true
      } catch (err) {
        updateSavedText(id, { isUsed: targetText.isUsed })
        const error = err instanceof Error ? err : new Error('Unknown error')
        setError(error)
        console.error('[useTexts.toggleUsed] Error:', error)
        return false
      }
    },
    [user, texts, updateSavedText]
  )

  // ========================================================================
  // [P2-02-F] loadMore 함수 (페이지네이션)
  // ========================================================================
  const loadMore = useCallback(async (): Promise<void> => {
    // 1. Guard Conditions
    if (!user) return
    if (!hasMore) return
    if (isLoading) return // 중복 요청 방지

    setSavedTextsLoading(true)

    try {
      // 2. Fetch Next Page
      const result = await getTexts(user.uid, filter, 20, lastDoc)

      // 3. Append to existing texts
      setSavedTexts([...texts, ...result.texts])

      // 4. Update pagination state
      setLastDoc(result.lastDoc)
      setHasMore(result.hasMore)

      setError(null)
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error')
      setError(error)
      console.error('[useTexts.loadMore] Error:', error)
    } finally {
      setSavedTextsLoading(false)
    }
  }, [user, hasMore, isLoading, filter, lastDoc, texts, setSavedTexts, setSavedTextsLoading])

  // ========================================================================
  // [P2-02-F] refresh 함수
  // ========================================================================
  const refresh = useCallback(async (): Promise<void> => {
    // 페이지네이션 상태 초기화
    setLastDoc(null)
    setHasMore(true)
    // 실시간 구독이 자동으로 새로고침 처리
  }, [])

  // ========================================================================
  // [P2-02-G] setFilter 함수
  // ========================================================================
  const setFilter = useCallback(
    (newFilter: Partial<FilterState>): void => {
      // 1. Update Store Filter
      setSavedTextsFilter(newFilter)

      // 2. Reset Pagination (필터 변경 시 처음부터)
      setLastDoc(null)
      setHasMore(true)
    },
    [setSavedTextsFilter]
  )

  // ========================================================================
  // [P5-02] 휴지통 항목 필터링 (삭제된 항목만)
  // ========================================================================
  const deletedTexts = texts.filter((t) => t.isDeleted === true)
  const activeTexts = texts.filter((t) => !t.isDeleted)

  // ========================================================================
  // Return
  // ========================================================================
  return {
    // State (활성 항목만 반환)
    texts: activeTexts,
    deletedTexts,  // [P5-02] 휴지통 항목
    isLoading,
    error,
    hasMore,

    // CRUD
    saveText,
    editText,
    removeText,
    toggleFavorite,  // [P3-01] 즐겨찾기 토글
    toggleUsed,      // 레퍼런스 사용 상태 토글

    // [P5-02] 휴지통
    restoreText,
    permanentDeleteText,

    // Pagination
    loadMore,
    refresh,

    // Filter
    setFilter,
  }
}

// ============================================================================
// 기본 export
// ============================================================================
export default useTexts
